// src/belediye/services/halkMasasiApi.js

import belediyeApi from "./belediyeApi";

// ------------------------------------------------------------
// Debug flag
// ------------------------------------------------------------
const DEBUG = String(process.env.REACT_APP_DEBUG_HALK_MASASI || "").trim() === "1";
const log = (...args) => {
  if (DEBUG) console.log("[halkMasasiApi]", ...args);
};
const logError = (...args) => {
  if (DEBUG) console.error("[halkMasasiApi]", ...args);
};

// ------------------------------------------------------------
// Base paths
// ------------------------------------------------------------
const BASE = "/halk-masasi";
const CITIZENS = `${BASE}/citizens`;
const APPLICATIONS = `${BASE}/applications`;

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------
const stripUndefined = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
};

export const normalizePhone10 = (raw) => {
  const digits = String(raw ?? "").replace(/\D+/g, "");
  if (!digits) return "";
  if (digits.length === 11 && digits.startsWith("0")) return digits.slice(1);
  if (digits.length > 10) return digits.slice(-10);
  return digits;
};

const hasAnyFile = (payload) =>
  Boolean(payload?.image1 || payload?.image2 || payload?.document1 || payload?.document2);

// citizen object -> dotted keys (DRF multipart uyumu)
const flattenCitizenForFormData = (fd, citizen) => {
  if (!citizen || typeof citizen !== "object") return;

  const name = String(citizen.name ?? "").trim();
  const surname = String(citizen.surname ?? "").trim();

  const rawPhone = String(citizen.phone_number ?? "").trim();
  const p10 = normalizePhone10(rawPhone);
  const phoneNumber = p10 ? `0${p10}` : String(rawPhone || "").trim();

  const email = citizen.email == null ? "" : String(citizen.email).trim();

  if (name) fd.append("citizen.name", name);
  if (surname) fd.append("citizen.surname", surname);
  if (phoneNumber) fd.append("citizen.phone_number", phoneNumber);
  if (email) fd.append("citizen.email", email);
};

// FormData builder - citizen objesini dotted key’lere çevirir
const toFormData = (payload) => {
  const fd = new FormData();

  Object.entries(payload || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;

    if (k === "citizen" && typeof v === "object" && !Array.isArray(v) && !(v instanceof File)) {
      flattenCitizenForFormData(fd, v);
      return;
    }

    if (typeof v === "string" && v.trim() === "") return;

    if (Array.isArray(v)) {
      v.forEach((item) => {
        if (item === undefined || item === null) return;
        fd.append(k, item);
      });
      return;
    }

    if (typeof FileList !== "undefined" && v instanceof FileList) {
      Array.from(v).forEach((file) => fd.append(k, file));
      return;
    }

    fd.append(k, v);
  });

  return fd;
};

const normalizeCitizenPayload = (body) => {
  const next = { ...(body || {}) };

  const hasCitizenId = Boolean(next?.citizen_id);
  const hasCitizenObj = Boolean(next?.citizen && typeof next.citizen === "object");

  if (!hasCitizenId && !hasCitizenObj) {
    throw new Error("citizen_id veya citizen zorunlu");
  }

  if (!next?.unit_id) throw new Error("unit_id zorunlu");
  if (!next?.subject) throw new Error("subject zorunlu");
  if (!next?.description) throw new Error("description zorunlu");

  if (!hasCitizenId && hasCitizenObj) {
    const c = next.citizen || {};
    const p10 = normalizePhone10(c.phone_number);
    next.citizen = {
      ...c,
      phone_number: p10 ? `0${p10}` : c.phone_number,
    };
  }

  return next;
};

const postAssistedPayload = async (url, payload = {}) => {
  const body = normalizeCitizenPayload(stripUndefined(payload || {}));

  log("postAssistedPayload", {
    url,
    hasFiles: hasAnyFile(body),
    hasCitizenId: Boolean(body?.citizen_id),
    hasCitizenObj: Boolean(body?.citizen),
    unit_id: body?.unit_id,
  });

  if (!hasAnyFile(body)) {
    const { data } = await belediyeApi.post(url, body);
    return data;
  }

  const fd = toFormData(body);

  const { data } = await belediyeApi.post(url, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

// ------------------------------------------------------------
// Feature flag probe (fail-safe)
// ------------------------------------------------------------
export const probeHalkMasasiEnabled = async () => {
  try {
    await belediyeApi.get(`${CITIZENS}/recent/`, { params: { limit: 1 } });
    return true;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) return false;
    logError("probe error (fail-safe disabled):", status, err?.message);
    return false;
  }
};

// ------------------------------------------------------------
// Citizens
// ------------------------------------------------------------
export const resolveCitizenByPhone = async (phone, { returnNullOn404 = false } = {}) => {
  const p = normalizePhone10(phone);
  if (!p) throw new Error("phone zorunlu");

  try {
    const { data } = await belediyeApi.get(`${CITIZENS}/resolve/`, {
      params: stripUndefined({ phone: p }),
    });
    return data;
  } catch (err) {
    const status = err?.response?.status;
    if (returnNullOn404 && status === 404) return null;
    throw err;
  }
};

/**
 * Son sorgular
 * GET /halk-masasi/citizens/recent/?limit=10
 */
export const getRecentCitizenLookups = async ({ limit = 10 } = {}) => {
  const { data } = await belediyeApi.get(`${CITIZENS}/recent/`, {
    params: stripUndefined({ limit }),
  });
  return data;
};

// ------------------------------------------------------------
// Applications - Normal create
// ------------------------------------------------------------
export const assistedCreateApplication = async (payload = {}) => {
  return postAssistedPayload(`${APPLICATIONS}/assisted-create/`, payload);
};

// ------------------------------------------------------------
// Applications - OTP flow
// ------------------------------------------------------------
/**
 * OTP ön akışı.
 *
 * Backend davranışı:
 * - OTP kapalıysa: normal create response dönebilir
 * - OTP açıksa: session summary döner
 */
export const preSubmitOtpAssistedApplication = async (payload = {}) => {
  return postAssistedPayload(`${APPLICATIONS}/pre-submit-otp/`, payload);
};

/**
 * OTP yeniden gönder
 * Body: { session_token }
 */
export const resendAssistedOtp = async ({ sessionToken } = {}) => {
  const token = String(sessionToken || "").trim();
  if (!token) throw new Error("sessionToken zorunlu");

  const { data } = await belediyeApi.post(`${APPLICATIONS}/resend-otp/`, {
    session_token: token,
  });
  return data;
};

/**
 * OTP doğrula + finalize
 * Body: { session_token, code }
 */
export const submitVerifiedAssistedApplication = async ({ sessionToken, code } = {}) => {
  const token = String(sessionToken || "").trim();
  const otpCode = String(code || "").trim();

  if (!token) throw new Error("sessionToken zorunlu");
  if (!otpCode) throw new Error("code zorunlu");

  const { data } = await belediyeApi.post(`${APPLICATIONS}/submit-verified/`, {
    session_token: token,
    code: otpCode,
  });
  return data;
};

export const getApplicationPublicFeed = async (applicationId) => {
  if (!applicationId) throw new Error("applicationId zorunlu");

  try {
    const { data } = await belediyeApi.get(`${APPLICATIONS}/${applicationId}/feed/`);
    return data;
  } catch (err) {
    logError("getApplicationPublicFeed error:", err?.response?.status, err?.message);
    throw err;
  }
};

// ------------------------------------------------------------
// Backward-compatible aliases (opsiyonel)
// ------------------------------------------------------------
export const lookupCitizenByPhone = resolveCitizenByPhone;
export const createAssistedApplication = assistedCreateApplication;

// ------------------------------------------------------------
// Convenience default export
// ------------------------------------------------------------
const halkMasasiApi = {
  probeHalkMasasiEnabled,
  resolveCitizenByPhone,
  getRecentCitizenLookups,

  // normal
  assistedCreateApplication,

  // otp
  preSubmitOtpAssistedApplication,
  resendAssistedOtp,
  submitVerifiedAssistedApplication,

  // feed
  getApplicationPublicFeed,

  // aliases
  lookupCitizenByPhone,
  createAssistedApplication,
};

export default halkMasasiApi;